import React from 'react'
import style from './DebugControls.module.scss'
import {
    SimulationResponseType,
    SIMULATION_DESCRIPTIONS,
    SIMULATION_TO_ERROR_CODE,
} from './simulationService'

interface DebugControlsProps {
    simulatedResponse: SimulationResponseType
    setSimulatedResponse: (type: SimulationResponseType) => void
    simulateDonation: (type: Exclude<SimulationResponseType, null>) => void
    resetForm: () => void
    // Debug values
    debugValues?: {
        campaign?: { id: string; name?: string } | null
        plexAmount?: number
        plexBalance?: number | 'N/A'
        character?: { id: number; name: string } | null
        charactersTotal?: number
        errorCode?: string | null
    }
    // Character simulation
    onSimulateCharacters?: (count: number) => void
}

// Group the simulations by type
const GROUPED_SIMULATIONS = {
    success: ['success'] as SimulationResponseType[],
    fieldErrors: [
        'error-plex',
        'error-character',
        'error-invalid-amount',
    ] as SimulationResponseType[],
    globalErrors: [
        'error-server',
        'error-campaign',
        'error-session-expired',
        'error-duplicate-donation',
        'error-system-unavailable',
        'error-unexpected',
    ] as SimulationResponseType[],
}

const DebugControls: React.FC<DebugControlsProps> = ({
    simulatedResponse,
    setSimulatedResponse,
    simulateDonation,
    resetForm,
    debugValues,
    onSimulateCharacters,
}) => {
    const [debugMode, setDebugMode] = React.useState(false)
    const [selectedGroup, setSelectedGroup] = React.useState<
        'success' | 'fieldErrors' | 'globalErrors'
    >('fieldErrors')

    // Keyboard shortcut to toggle debug mode (Alt+D)
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent): void => {
            if (e.altKey && e.key === 'd') {
                setDebugMode(!debugMode)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [debugMode])

    // Log when component renders with different simulatedResponse
    React.useEffect(() => {
        console.log(
            'DebugControls rendering with simulatedResponse:',
            simulatedResponse
        )
    }, [simulatedResponse])

    if (!debugMode) return null

    // Helper to format campaign info
    const formatCampaignInfo = (): string => {
        if (!debugValues?.campaign) return 'Not selected'

        const { id, name } = debugValues.campaign
        // Handle cases where name might be undefined but id exists
        if (name) {
            return `${name} (${id})`
        }
        return `Campaign ID: ${id}`
    }

    // Add helper to show character count
    const getCharacterCountText = (): string => {
        if (debugValues?.character) {
            return '1 selected'
        }
        if (debugValues?.charactersTotal) {
            return `${debugValues.charactersTotal} available`
        }
        return 'None'
    }

    // Helper function to update simulation mode and trigger simulation
    const setSimulationMode = (type: SimulationResponseType): void => {
        console.log(`Setting simulation mode to: ${type || 'Real API'}`)
        setSimulatedResponse(type)
        if (type !== null) {
            simulateDonation(type)
        }
    }

    // Helper to get the currently selected group's simulations
    const getCurrentGroupSimulations = (): SimulationResponseType[] => {
        return GROUPED_SIMULATIONS[selectedGroup]
    }

    return (
        <div className={style.debugPanel}>
            <h3>Debug Controls</h3>

            <div className={style.columnHeaders}>
                <h4>Simulation Controls</h4>
                <h4>Current Values</h4>
            </div>

            <div className={style.debugPanelContent}>
                {/* Simulation Controls */}
                <div className={style.buttonsSection}>
                    {/* Group Selection Tabs */}
                    <div className={style.tabsContainer}>
                        <button
                            type="button"
                            className={`${style.tabButton} ${
                                selectedGroup === 'success'
                                    ? style.activeTab
                                    : ''
                            }`}
                            onClick={() => setSelectedGroup('success')}
                        >
                            Success
                        </button>
                        <button
                            type="button"
                            className={`${style.tabButton} ${
                                selectedGroup === 'fieldErrors'
                                    ? style.activeTab
                                    : ''
                            }`}
                            onClick={() => setSelectedGroup('fieldErrors')}
                        >
                            Field Errors
                        </button>
                        <button
                            type="button"
                            className={`${style.tabButton} ${
                                selectedGroup === 'globalErrors'
                                    ? style.activeTab
                                    : ''
                            }`}
                            onClick={() => setSelectedGroup('globalErrors')}
                        >
                            Global Errors
                        </button>
                    </div>

                    {/* Error Simulation Buttons */}
                    <div className={style.simulationButtons}>
                        {getCurrentGroupSimulations().map((simType) => (
                            <button
                                key={simType}
                                type="button"
                                onClick={() => setSimulationMode(simType)}
                                className={
                                    simulatedResponse === simType
                                        ? style.activeButton
                                        : ''
                                }
                            >
                                {SIMULATION_DESCRIPTIONS[simType]}
                            </button>
                        ))}
                    </div>

                    {/* Character Simulation Controls */}
                    {onSimulateCharacters && (
                        <div className={style.characterSimulation}>
                            <h4>Character Simulation</h4>
                            <div className={style.characterButtons}>
                                {[0, 1, 2, 3].map((count) => (
                                    <button
                                        key={`char-${count}`}
                                        type="button"
                                        onClick={() =>
                                            onSimulateCharacters(count)
                                        }
                                        className={
                                            debugValues?.charactersTotal ===
                                            count
                                                ? style.activeButton
                                                : ''
                                        }
                                    >
                                        {count === 0
                                            ? 'No Characters'
                                            : `${count} Character${
                                                  count > 1 ? 's' : ''
                                              }`}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Additional Controls */}
                    <div className={style.additionalControls}>
                        <button
                            type="button"
                            onClick={() => {
                                console.log('Switching to real API mode')
                                setSimulationMode(null)
                            }}
                            className={
                                simulatedResponse === null
                                    ? style.activeButton
                                    : ''
                            }
                        >
                            Use Real API
                        </button>
                        <button
                            type="button"
                            onClick={resetForm}
                            className={style.resetButton}
                        >
                            Reset Form
                        </button>
                    </div>

                    <div className={style.simulationMode}>
                        Current mode:{' '}
                        <strong>
                            {simulatedResponse
                                ? SIMULATION_DESCRIPTIONS[simulatedResponse]
                                : 'Real API'}
                        </strong>
                    </div>
                </div>

                {/* Debug Values Section */}
                {debugValues && (
                    <div className={style.debugValuesSection}>
                        <div className={style.debugValueItem}>
                            <span>Campaign:</span>
                            {formatCampaignInfo()}
                        </div>
                        <div className={style.debugValueItem}>
                            <span>Plex Amount:</span>
                            {debugValues.plexAmount ?? 0}
                        </div>
                        <div className={style.debugValueItem}>
                            <span>Plex Balance:</span>
                            {debugValues.plexBalance ?? 'Unknown'}
                        </div>
                        <div className={style.debugValueItem}>
                            <span>Characters:</span>
                            {getCharacterCountText()}
                        </div>
                        <div className={style.debugValueItem}>
                            <span>Selected Character:</span>
                            {debugValues.character
                                ? `${debugValues.character.name} (${debugValues.character.id})`
                                : 'Not selected'}
                        </div>
                        {debugValues.errorCode && (
                            <div className={style.debugValueItem}>
                                <span>Error Code:</span>
                                {debugValues.errorCode}
                            </div>
                        )}
                        <div className={style.debugValueItem}>
                            <span>Simulation Mode:</span>
                            {simulatedResponse
                                ? SIMULATION_DESCRIPTIONS[simulatedResponse]
                                : 'Real API'}
                        </div>
                        {simulatedResponse && (
                            <div className={style.debugValueItem}>
                                <span>Error Code:</span>
                                {simulatedResponse === 'success'
                                    ? 'None'
                                    : SIMULATION_TO_ERROR_CODE[
                                          simulatedResponse
                                      ]}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default DebugControls
